<template>
  <header class="app-header" :class="{ 'is-scrolled': isScrolled, 'menu-open': menuOpen, 'mega-open': !!activeMegaMenu }">
    <div class="header-inner container">
      <router-link to="/" class="logo" @click.native="closeMenu">
        <div class="logo-icon" v-if="siteLogoUrl">
          <img :src="siteLogoUrl" :alt="(configData && configData.SiteName) || siteConfig.companyName" />
        </div>
        <!-- <div class="logo-text" v-if="configData">
          <strong>{{ configData.SiteName }}</strong>
          <small>{{ siteConfig.companyNameEn }}</small>
        </div> -->
      </router-link>

      <nav class="nav-menu" :class="{ open: menuOpen }">
        <template v-for="item in navItems">
          <!-- 带下拉菜单 -->
          <div
            v-if="hasMegaMenu(item)"
            :key="'mega-' + item.path"
            class="nav-item-wrapper"
            @mouseenter="onNavEnter(item)"
            @mouseleave="onNavLeave"
          >
            <nav-link
              :to="item.path"
              :href="item.href"
              :external="item.external"
              :link-class="[
                'nav-item',
                {
                  active: isActive(item),
                  'mega-active': activeMegaMenu === getNavItemKey(item)
                }
              ]"
              @click="closeMenu"
            >
              {{ item.name }}
              <i class="el-icon-arrow-down nav-arrow"></i>
            </nav-link>

            <nav-dropdown
              v-if="isSimpleMenu(item)"
              :visible="activeMegaMenu === getNavItemKey(item)"
              :items="getFlatMenuItems(item)"
              @enter="onMegaEnter"
              @leave="onMegaLeave"
              @navigate="closeMegaMenu"
            />

            <div v-if="menuOpen" class="mobile-sub-menu">
              <template v-for="(sub, idx) in getFlatMenuItems(item)">
                <span
                  v-if="sub.disabled"
                  :key="'disabled-' + idx"
                  class="mobile-sub-item is-disabled"
                >
                  {{ sub.title }}
                </span>
                <nav-link
                  v-else
                  :key="idx"
                  :to="sub.path"
                  :href="sub.href"
                  :external="sub.external"
                  link-class="mobile-sub-item"
                  @click="closeMenu"
                >
                  {{ sub.title }}
                </nav-link>
              </template>
            </div>
          </div>

          <!-- 普通导航项 -->
          <nav-link
            v-else
            :key="item.megaMenuKey || item.path"
            :to="item.path"
            :href="item.href"
            :external="item.external"
            :link-class="['nav-item', { active: isActive(item) }]"
            @click="closeMenu"
          >
            {{ item.name }}
          </nav-link>
        </template>
      </nav>

      <div class="header-right">
        <a :href="'tel:' + siteConfig.phone" class="phone-info">
          <img :src="phoneIcon" alt="联系电话" class="phone-icon" />
          <span class="phone-label">联系电话：</span>
          <span class="phone-number">{{ siteConfig.phoneDisplay }}</span>
        </a>
        <!-- <button class="consult-btn" @click="openConsult">立即咨询</button> -->
        <button class="menu-toggle" @click="toggleMenu" aria-label="菜单">
          <i :class="menuOpen ? 'el-icon-close' : 'el-icon-s-unfold'"></i>
        </button>
      </div>
    </div>

    <nav-mega-menu
      v-if="currentMegaMenu && !isSimpleMenu(currentNavItem)"
      :visible="!!activeMegaMenu"
      :menu="currentMegaMenu"
      @enter="onMegaEnter"
      @leave="onMegaLeave"
      @navigate="closeMegaMenu"
    />
  </header>
</template>

<script>
import { siteConfig } from '@/data/site'
import { mapActions, mapGetters } from 'vuex'
import { findNavItemByKey, hasNavSubmenu, isSimpleMegaMenu, resolveMegaMenu } from '@/utils/nav'
import NavLink from '@/components/common/NavLink.vue'
import NavMegaMenu from './NavMegaMenu.vue'
import NavDropdown from './NavDropdown.vue'
import { fetchConfigSetting } from '@/api/anqicms'
import { anqicmsConfig } from '@/config/anqicms'
import { resolveImageUrl, toOriginalImageUrl } from '@/utils/archive'
import phoneIcon from '@/assets/images/phone-icon.png'

export default {
  name: 'AppHeader',
  components: { NavMegaMenu, NavDropdown, NavLink },
  data() {
    return {
      phoneIcon,
      siteConfig,
      isScrolled: false,
      menuOpen: false,
      activeMegaMenu: null,
      hideTimer: null,
      configData: null,
    }
  },
  computed: {
    ...mapGetters(['headerNavItems', 'navFromApi']),
    navItems() {
      return this.headerNavItems
    },
    /** 配置里的 SiteLogo 可能为 thumb_ 缩略图，转原图并补全相对路径 */
    siteLogoUrl() {
      const logo = this.configData && this.configData.SiteLogo
      if (!logo) return ''
      return resolveImageUrl(toOriginalImageUrl(logo), anqicmsConfig.apiBase)
    },
    currentMegaMenu() {
      if (!this.activeMegaMenu) return null
      const navItem = findNavItemByKey(this.navItems, this.activeMegaMenu)
      return resolveMegaMenu(navItem, this.activeMegaMenu, { navFromApi: this.navFromApi })
    },
    currentNavItem() {
      if (!this.activeMegaMenu) return null
      return findNavItemByKey(this.navItems, this.activeMegaMenu)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.loadConfig()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    this.clearHideTimer()
  },
  methods: {
    ...mapActions(['openRequirementDialog']),
    async loadConfig() {
      try {
        const res = await fetchConfigSetting()
        if(res.code === 0) {
          this.configData = res.data
        }
      } catch (e) {
        console.warn('获取配置失败', e.message)
      }
    },
    hasMegaMenu(item) {
      return hasNavSubmenu(item, { navFromApi: this.navFromApi })
    },
    getNavItemKey(item) {
      return item.megaMenuKey || item.path
    },
    isSimpleMenu(item) {
      const megaMenu = resolveMegaMenu(item, this.getNavItemKey(item), { navFromApi: this.navFromApi })
      return isSimpleMegaMenu(megaMenu)
    },
    getFlatMenuItems(item) {
      const megaMenu = resolveMegaMenu(item, item.megaMenuKey || item.path, { navFromApi: this.navFromApi })
      if (!megaMenu) return []
      return megaMenu.columns.flatMap(col => col.items)
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    },
    isActive(item) {
      const path = item.path || '/'
      if (path === '/') {
        return this.$route.path === '/'
      }
      if (item.external) return false
      return this.$route.path.startsWith(path.split('#')[0])
    },
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },
    onNavEnter(item) {
      if (window.innerWidth <= 768) return
      this.clearHideTimer()
      this.activeMegaMenu = this.getNavItemKey(item)
    },
    onNavLeave() {
      if (window.innerWidth <= 768) return
      this.scheduleHide()
    },
    onMegaEnter() {
      this.clearHideTimer()
    },
    onMegaLeave() {
      this.scheduleHide()
    },
    scheduleHide() {
      this.clearHideTimer()
      this.hideTimer = setTimeout(() => {
        this.activeMegaMenu = null
      }, 150)
    },
    closeMegaMenu() {
      this.activeMegaMenu = null
      this.closeMenu()
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen
      if (this.menuOpen) {
        this.activeMegaMenu = null
      }
    },
    closeMenu() {
      this.menuOpen = false
      this.activeMegaMenu = null
    },
    openConsult() {
      this.closeMenu()
      this.openRequirementDialog()
    }
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: $white;
  transition: $transition;

  &.is-scrolled {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  &.mega-open {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: $header-height;
  position: relative;
  z-index: 1001;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    height: 50px;
    width: auto;
    max-width: 160px;
    display: block;
    object-fit: contain;
  }
}

.logo-text {
  display: flex;
  flex-direction: column;

  strong {
    font-size: 18px;
    color: $text-primary;
    line-height: 1.2;
  }

  small {
    font-size: 11px;
    color: $text-light;
  }
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-item-wrapper {
  position: relative;
}

::v-deep .nav-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: relative;
  padding: 8px 16px;
  font-size: 15px;
  color: $text-secondary;
  transition: $transition;
  border-radius: 4px;
  white-space: nowrap;

  .nav-arrow {
    font-size: 12px;
    transition: transform 0.2s, color 0.3s;
  }

  &::after {
    content: '';
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 2px;
    height: 2px;
    border-radius: 1px;
    @include brand-gradient-bg;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover,
  &.active,
  &.mega-active {
    @include brand-gradient-text;

    .nav-arrow {
      @include brand-gradient-text;
    }

    &::after {
      opacity: 1;
    }
  }

  &.mega-active .nav-arrow {
    transform: rotate(180deg);
  }

  &.active {
    font-weight: 600;
  }
}

.mobile-sub-menu {
  display: none;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.phone-info {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  white-space: nowrap;
  transition: $transition;

  .phone-icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    object-fit: contain;
  }

  .phone-label {
    font-size: 14px;
    color: $text-secondary;
  }

  .phone-number {
    font-size: 14px;
    color: $text-primary;
    font-weight: 600;
  }

  &:hover {
    .phone-number {
      color: $primary-color;
    }
  }
}

.consult-btn {
  padding: 8px 20px;
  background: $primary-color;
  color: $white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: $transition;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 102, 255, 0.3);

  &:hover {
    background: $primary-dark;
  }
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: $text-primary;
  cursor: pointer;
  padding: 4px;
}

.header-border {
  height: 3px;
  background: $primary-color;
  position: relative;
  z-index: 1001;
}

@media (max-width: 992px) {
  .phone-info {
    display: none;
  }
}

@media (max-width: 768px) {
  .header-inner {
    height: $header-height-mobile;
  }

  .logo-icon img {
    height: 36px;
    max-width: 120px;
  }

  .consult-btn {
    display: none;
  }

  .menu-toggle {
    display: block;
  }

  .nav-menu {
    position: fixed;
    top: $header-height-mobile;
    left: 0;
    right: 0;
    background: $white;
    flex-direction: column;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-120%);
    opacity: 0;
    transition: $transition;
    z-index: 999;
    max-height: calc(100vh - #{$header-height-mobile});
    overflow-y: auto;

    &.open {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .nav-item-wrapper {
    width: 100%;
  }

  ::v-deep .nav-item {
    width: 100%;
    justify-content: center;
    padding: 12px;
    border-bottom: 1px solid $border-color;

    .nav-arrow {
      display: none;
    }
  }

  .mobile-sub-menu {
    display: block;
    padding: 0 12px 8px;
    background: $bg-gray;
    border-bottom: 1px solid $border-color;
  }

  ::v-deep .mobile-sub-item {
    display: block;
    padding: 10px 16px;
    font-size: 13px;
    color: $text-secondary;
    border-bottom: 1px dashed $border-color;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      color: $primary-color;
    }

    &.is-disabled {
      cursor: default;

      &:hover {
        color: $text-secondary;
      }
    }
  }
}
</style>
